import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { UpsertSupplierRequest, AiSupplierItem, Supplier, SupplierCategory } from '../models/supplier.model';
import { Pagination, Response } from '../../../../_shared/models';
import { SuppliersListPaginationRequest } from '../models/supplier-pagination-request.model';

@Injectable({
  providedIn: 'root'
})
export class SuppliersService {

  constructor(private http: HttpClient) { }

  upsertSupplierAsync(request: UpsertSupplierRequest): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${environment.apiEndpoint}/suppliers`, request));
  }

  getSupplierCategoriesAsync(): Promise<Response<SupplierCategory[]>> {
    return firstValueFrom(this.http.get<Response<SupplierCategory[]>>(`${environment.apiEndpoint}/suppliers/categories`));
  }

  deleteSupplierAsync(id: number): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${environment.apiEndpoint}/suppliers/${id}`));
  }

  getSuppliersAsync(request: SuppliersListPaginationRequest, cancellation: Subject<void>): Promise<Response<Pagination<Supplier[]>>> {
    return firstValueFrom(this.http.post<Response<Pagination<Supplier[]>>>(`${environment.apiEndpoint}/suppliers/paginated-suppliers`, request).pipe(takeUntil(cancellation)));
  }

  /**
   * Get AI supplier optimization recommendations
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async getAiSupplierOptimizationsAsync(): Promise<AiSupplierItem[]> {
    const mockData = this.generateAiSupplierMockData();

    // Simulate API call with delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    return mockData;
  }

  /**
   * Get AI supplier optimization by ID
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async getAiSupplierOptimizationByIdAsync(id: number): Promise<AiSupplierItem | undefined> {
    const mockData = this.generateAiSupplierMockData();

    await new Promise(resolve => setTimeout(resolve, 500));
    return mockData.find(s => s.id === id);
  }

  /**
   * Compare suppliers for a specific part
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async compareSuppliersAsync(partNumber: number): Promise<AiSupplierItem[]> {
    // Mock comparison data - would normally filter by part number
    const mockComparisons: AiSupplierItem[] = [
      { id: 1, supplierName: 'Supplier A', partName: "Oil Filter", cost: 350, partNumber: partNumber },
      { id: 2, supplierName: 'Supplier B', partName: "Oil Filter", cost: 380, partNumber: partNumber },
      { id: 3, supplierName: 'Supplier C', partName: "Oil Filter", cost: 340, partNumber: partNumber },
    ];

    await new Promise(resolve => setTimeout(resolve, 1500));
    return mockComparisons;
  }

  /**
   * Generate AI supplier optimization mock data for testing
   * TODO: Remove when backend API is ready
   */
  private generateAiSupplierMockData(): AiSupplierItem[] {
    return [
      { id: 1, supplierName: 'Supplier A', partName: "Volvo FH16 - Oil Filterswew ewewecwec wee", cost: 350, partNumber: 21707132 },
      { id: 2, supplierName: 'Supplier B', partName: "Volvo FH16 - Fuel Filter", cost: 420, partNumber: 21983475 },
      { id: 3, supplierName: 'Supplier C', partName: "Scania R500 - Brake Pad Set", cost: 780, partNumber: 18576432 },
      { id: 4, supplierName: 'Supplier D', partName: "MAN TGX - Air Filter", cost: 390, partNumber: 20458931 },
      { id: 5, supplierName: 'Supplier A', partName: "DAF XF - Oil Filter", cost: 330, partNumber: 22764311 },
      { id: 6, supplierName: 'Supplier B', partName: "Mercedes Actros - Fuel Filter", cost: 460, partNumber: 19873452 },
      { id: 7, supplierName: 'Supplier C', partName: "Volvo FH16 - Brake Disc", cost: 950, partNumber: 23114578 },
      { id: 8, supplierName: 'Supplier D', partName: "Scania R440 - Clutch Kit", cost: 1250, partNumber: 17654329 },
      { id: 9, supplierName: 'Supplier A', partName: "MAN TGS - Air Dryer Cartridge", cost: 520, partNumber: 21457893 },
      { id: 10, supplierName: 'Supplier B', partName: "DAF CF - Cabin Filter", cost: 280, partNumber: 20987654 },
      { id: 11, supplierName: 'Supplier C', partName: "Volvo FMX - Fuel Filter", cost: 435, partNumber: 22349876 },
      { id: 12, supplierName: 'Supplier D', partName: "Mercedes Arocs - Brake Pad Set", cost: 810, partNumber: 19384756 },
      { id: 13, supplierName: 'Supplier A', partName: "Scania R620 - Oil Filter", cost: 365, partNumber: 21876549 },
      { id: 14, supplierName: 'Supplier B', partName: "MAN TGX - Fuel Pump", cost: 1390, partNumber: 18765439 },
      { id: 15, supplierName: 'Supplier C', partName: "Volvo FH16 - Turbo Hose", cost: 640, partNumber: 23659874 },
      { id: 16, supplierName: 'Supplier D', partName: "DAF XF - Brake Disc", cost: 910, partNumber: 20547631 },
      { id: 17, supplierName: 'Supplier A', partName: "Mercedes Actros - Air Filter", cost: 405, partNumber: 19928374 },
      { id: 18, supplierName: 'Supplier B', partName: "Scania P410 - Fuel Filter", cost: 395, partNumber: 22456781 },
      { id: 19, supplierName: 'Supplier C', partName: "MAN TGM - Oil Filter", cost: 345, partNumber: 21239857 },
      { id: 20, supplierName: 'Supplier D', partName: "Volvo FH16 - Water Pump", cost: 1570, partNumber: 23874651 },
    ];
  }
}